import {ColumnDeclaration} from '../../lib/wjgrid-utils';
import {CollectionView, DataType} from '@mescius/wijmo';
import {MonacoEditorComponent} from '../../components/monaco-editor/monaco-editor.component';
import {FlexGrid} from '@mescius/wijmo.grid';
import {NgDynamicComponentsService} from '../../service/ng-dynamic-components.service';
import { PortfolioSegmentDataContext } from '../../data/portfolio-segment-data-context.service';
import {StringUtils, trimIndent} from '../../lib/strings';
import {AccountData} from '../../../api-client-kt/src';
import {DataDictionaryService} from '../../data/data-dictionary';
import {sortBy} from 'es-toolkit';
import {Stdlib} from '../../lib/stdlib';
import {JsonHelper} from '../../lib/json';
import {PortfolioSegmentWorkbook} from '../../data/portfolio-segment-workbook';

export class DevToolsFormulas {

    static PyAccountFormulaColumns: ColumnDeclaration[] = [
        {header: 'Column', binding: 'header', dataType: DataType.String, width: '*', isReadOnly: true},
        {header: 'JSON Name', binding: 'jsonName', dataType: DataType.String, width: 120, isReadOnly: true},
        {header: 'Data Type', binding: 'dataType', dataType: DataType.String, width: 100, isReadOnly: true},
        {header: 'Group', binding: 'group', dataType: DataType.String, width: 130, isReadOnly: true},
        {header: 'Send', binding: 'includeInSend', dataType: DataType.Boolean, width: 70, align: 'center'},
        {header: 'Receive', binding: 'includeInReceive', dataType: DataType.Boolean, width: 70, align: 'center'},
    ];

    static generateAccountFormulaGridData(extraAttr: string[]): CollectionView {
        const allColumnsUnsorted = DataDictionaryService.AccountDataDictionary.map(col => ({
            header: col.name,
            jsonName: col.attribute,
            dataType: col.pfmDataType,
            group: col.group,
            includeInSend: false,
            includeInReceive: false,
        }));
        const allColumns = sortBy(allColumnsUnsorted, ['group', 'jsonName']);

        const allColumnsMap = new Map<string, any>(allColumns.map(c => [c.jsonName, c]));
        const allAttrsToSend = [...PortfolioSegmentWorkbook.AccountAttributesToSend, ...extraAttr];
        const allAttrsToReceive = PortfolioSegmentWorkbook.AccountAttributesToReceive;

        allAttrsToSend.forEach(cd => Stdlib.let(allColumnsMap.get(cd), it => it.includeInSend = true));
        allAttrsToReceive.forEach(cd => Stdlib.let(allColumnsMap.get(cd), it => it.includeInReceive = true));

        const allColumnsCv = new CollectionView(allColumns);
        return allColumnsCv;
    }

    static async generateAccountJson(grid: FlexGrid, pfSegData: PortfolioSegmentDataContext, dynCompSvc: NgDynamicComponentsService) {
        const valuesToSend = this.extractValuesToSendFromGrid(grid);
        const data = pfSegData.getAccountData();
        const jsonItems = data.map(it => {
            const accountData: Record<string, any> = {};
            valuesToSend.forEach(valueRef => {
                const attr = valueRef.jsonName as keyof AccountData;
                accountData[attr] = JsonHelper.convertValueToJson(it[attr], valueRef.dataType);
            });
            return accountData;
        });
        const json = JSON.stringify(jsonItems, null, 2);

        const dlg = await dynCompSvc.createComponentDialog(MonacoEditorComponent, {
            title: 'Account JSON',
            width: 'min(80vw, 1000px)',
            input: { initialValue: json, language: 'json' },
        });
    }

    static async generateAccountPython(grid: FlexGrid, pfSegData: PortfolioSegmentDataContext, dynCompSvc: NgDynamicComponentsService) {
        const valuesToSend = this.extractValuesToSendFromGrid(grid);
        const data = pfSegData.getAccountData();

        const dataArray = data.map(it => {
            return valuesToSend.map(valueRef => {
                const attr = valueRef.jsonName as keyof AccountData;
                const value = JsonHelper.convertValueToJson(it[attr], valueRef.dataType);
                if (value === undefined || value === null) {
                    return 'None';
                } else {
                    switch (valueRef.dataType) {
                        case 'date':
                        case 'datetime':
                        case 'timestamp':
                            return `__dt("${value}")`;

                        case 'boolean':
                            return value ? 'True' : 'False';

                        case 'text':
                            return StringUtils.escapeJsString(value);

                        default:
                            return value;
                    }
                }
            });
        });

        const dataPythonSource = dataArray.map(row => `[${row.join(', ')}]`).join(',\n');
        const columns = JSON.stringify(valuesToSend.map(it => it.jsonName));

        const pythonSourceTemplate = trimIndent`
            import pandas as pd
            def __dt(value): return pd.to_datetime(value, errors='coerce')
            pfm_accounts_data=[
            {data}
            ]
            pfm_accounts_df = pd.DataFrame(columns={columns}, data=pfm_accounts_data)
        `;
        const pythonSource = pythonSourceTemplate.replace('{columns}', columns).replace('{data}', dataPythonSource);

        const dlg = await dynCompSvc.createComponentDialog(MonacoEditorComponent, {
            title: 'Account Python Data',
            width: 'min(80vw, 1000px)',
            input: { initialValue: pythonSource, language: 'python' },
        });
    }

    private static idFirstSorter(a: string, b: string): number {
        if (a === 'id') return -1;
        if (b === 'id') return 1;
        return StringUtils.compare(a, b);
    }

    private static extractValuesToSendFromGrid(grid: FlexGrid<any>) {
        const accountFormulaCols = grid.itemsSource as CollectionView;
        const valuesToSend = accountFormulaCols.sourceCollection
            .filter(it => it.includeInSend)
            .sort((a, b) => DevToolsFormulas.idFirstSorter(a.jsonName, b.jsonName));
        return valuesToSend;
    }
}
